import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Printer,
  Share2,
  X,
  Package,
  Loader2,
  ShoppingCart,
  ListChecks,
  Check,
  Trash2,
  Image as ImageIcon,
  Building2,
  Inbox,
} from "lucide-react";
import { smartMatch } from "../../utils/arabicSearch";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Build a haystack with cost_price + name so manager can search by either.
const productHaystack = (p) =>
  `${p.name_ar || ""} ${p.name_en || ""} ${p.weight_per_piece || ""} ${p.units_per_carton || ""} ${p.category || ""} ${p.brand_name || ""}`;

// ─── Picker (same UX as PriceListTab) ───
const ProductPickerModal = ({ products, picked, onClose, onDone }) => {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState({ ...picked });

  const filtered = useMemo(() => {
    if (!q.trim()) return products;
    return products.filter((p) => smartMatch(productHaystack(p), q));
  }, [products, q]);

  const toggle = (p) => {
    setDraft((d) => {
      const next = { ...d };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = { product: p, cartons: 1 };
      return next;
    });
  };

  const pickedCount = Object.keys(draft).length;

  return (
    <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="purchase-picker-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-l from-[#7C3AED] to-[#A855F7] text-white p-4 shrink-0 flex justify-between items-start">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2"><ListChecks className="w-5 h-5" /> اختيار مواد قائمة الشراء</h3>
            <p className="text-[10px] opacity-90 mt-0.5">اضغط مادة لإضافتها — حدّد عدد الكراتين لاحقاً.</p>
          </div>
          <button onClick={onClose} data-testid="purchase-picker-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-3 space-y-2 overflow-y-auto flex-1">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6E64]" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="purchase-picker-search"
              placeholder="ابحث ذكياً..."
              className="w-full pr-9 pl-4 py-2 rounded-full border-2 border-[#A855F7]"
            />
          </div>
          {filtered.length === 0 ? (
            <p className="text-center py-6 text-[#5C6E64] text-sm">لا توجد نتائج</p>
          ) : (
            filtered.map((p) => {
              const isPicked = !!draft[p.id];
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p)}
                  data-testid={`purchase-picker-row-${p.id}`}
                  className={`w-full rounded-xl p-2 flex items-center gap-2 border-2 transition-colors text-right ${
                    isPicked ? "bg-[#F5F3FF] border-[#A855F7]" : "bg-white border-[#E4EBE6] hover:border-[#A855F7]"
                  }`}
                >
                  {p.image_path ? (
                    <img src={`${API}/files/${p.image_path}`} className="w-12 h-12 rounded-lg object-cover" alt="" />
                  ) : <div className="w-12 h-12 bg-[#F3F6F4] rounded-lg flex items-center justify-center"><Package className="w-4 h-4 text-[#5C6E64]" /></div>}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate flex items-center gap-1">
                      {isPicked && <span className="text-[#7C3AED]">✓</span>}
                      {p.name_ar}
                    </p>
                    <p className="text-[10px] text-[#5C6E64]">
                      سعر الشراء: <span className="font-black text-[#7C3AED]" dir="ltr">{fmt(p.cost_price || 0)} د.ع</span>
                      {p.weight_per_piece ? <> · <span dir="ltr">{p.weight_per_piece}</span></> : null}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="bg-white border-t-2 border-[#E4EBE6] p-3 flex items-center justify-between gap-2 shrink-0">
          <div className="text-xs">
            <p className="text-[#5C6E64]">مُختار</p>
            <p data-testid="purchase-picker-count" className="font-black text-lg text-[#7C3AED]" dir="ltr">{pickedCount}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] text-xs font-bold py-2 px-3 rounded-full">إلغاء</button>
            <button
              onClick={() => onDone(draft)}
              disabled={pickedCount === 0}
              data-testid="purchase-picker-attach"
              className="bg-[#7C3AED] hover:bg-[#6d28d9] disabled:bg-[#E9D5FF] text-white text-xs font-bold py-2 px-4 rounded-full flex items-center gap-1"
            >
              <Check className="w-3.5 h-3.5" /> إرفاق ({pickedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main tab ───
const PurchaseListTab = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  // picked is keyed by product id and stores {product, cartons}
  const [picked, setPicked] = useState({});
  // Supplier selection — REQUIRED before exporting to admin. Manager must
  // pick a registered supplier (from حسابات المُجهّزين) so the approval
  // creates an official invoice tied to that supplier's ledger.
  const [supplierId, setSupplierId] = useState("");
  const [showImages, setShowImages] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API}/admin/products`),
          axios.get(`${API}/admin/suppliers`),
        ]);
        setProducts(pRes.data || []);
        setSuppliers(sRes.data || []);
      } catch { toast.error("فشل التحميل"); }
      finally { setLoading(false); }
    })();
  }, []);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === supplierId) || null,
    [suppliers, supplierId]
  );
  const companyName = selectedSupplier?.name || "";

  const items = useMemo(() => Object.values(picked), [picked]);

  const removeItem = (id) => {
    setPicked((p) => { const n = { ...p }; delete n[id]; return n; });
  };

  const setCartons = (id, n) => {
    setPicked((p) => ({ ...p, [id]: { ...p[id], cartons: Math.max(0, Number(n) || 0) } }));
  };

  const setCostPrice = (id, n) => {
    // Allow operator to override cost_price for this purchase (e.g. negotiated price)
    setPicked((p) => ({
      ...p,
      [id]: { ...p[id], product: { ...p[id].product, _override_cost: Math.max(0, Number(n) || 0) } },
    }));
  };

  // 🏷️ Per-row A+ / B+ tier price inputs. Persisted locally as
  // `_pending_price` (A+) and `_pending_wholesale_price` (B+) until the
  // operator commits them (either via onBlur inline save, or when the
  // proposal is exported to admin — see `handleExportToAdmin`).
  const setPriceAPlus = (id, n) => {
    setPicked((p) => ({
      ...p,
      [id]: { ...p[id], product: { ...p[id].product, _pending_price: Math.max(0, Number(n) || 0) } },
    }));
  };
  const setPriceBPlus = (id, n) => {
    setPicked((p) => ({
      ...p,
      [id]: { ...p[id], product: { ...p[id].product, _pending_wholesale_price: Math.max(0, Number(n) || 0) } },
    }));
  };

  // Commit tier prices to the actual product record. Fires when the operator
  // leaves the price input (onBlur) — only if the value actually changed.
  // Silent failure with a subtle toast; UX is fire-and-forget.
  const saveTierPrice = async (product, field) => {
    const key = field === "price" ? "_pending_price" : "_pending_wholesale_price";
    const next = product[key];
    const current = field === "price" ? product.price : product.wholesale_price;
    if (next == null || Number(next) === Number(current || 0)) return;
    try {
      await axios.patch(`${API}/admin/products/${product.id}`, { [field]: Number(next) });
      // Persist to the in-memory product so future re-renders reflect it
      setPicked((p) => {
        const cur = p[product.id];
        if (!cur) return p;
        return {
          ...p,
          [product.id]: {
            ...cur,
            product: {
              ...cur.product,
              [field]: Number(next),
              _price_saved_at: Date.now(),
              [key]: undefined,
            },
          },
        };
      });
      toast.success(field === "price" ? "تم حفظ سعر A+" : "تم حفظ سعر B+", { duration: 1500 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل حفظ السعر");
    }
  };

  // Per-row name override — stored locally on the picked item ONLY (does not
  // touch /api/admin/products). Lets the operator shorten "دجاج بوادي 900
  // قطعة كبيرة" to just "دجاج 900" for the supplier's purchase list. Cleared
  // when the row is removed; reset back to the original by clearing the input.
  const setDisplayName = (id, v) => {
    setPicked((p) => ({ ...p, [id]: { ...p[id], display_name: v } }));
  };
  const itemName = (it) =>
    (it.display_name != null ? it.display_name : it.product.name_ar) || "";

  const lineTotal = (it) => {
    const cost = it.product._override_cost ?? it.product.cost_price ?? 0;
    return Number(cost) * Number(it.cartons || 0);
  };
  const grandTotal = useMemo(() => items.reduce((s, it) => s + lineTotal(it), 0), [items]);
  const totalCartons = useMemo(() => items.reduce((s, it) => s + Number(it.cartons || 0), 0), [items]);

  const handlePrint = () => {
    const node = printRef.current;
    if (!node) return;
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>قائمة شراء - جملتي</title>
<style>
@page { size: A4; margin: 12mm }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: 'Tajawal', system-ui, sans-serif; margin: 0; padding: 16px; color: #111; direction: rtl; }
h1 { color: #7C3AED; margin: 0 0 4px; font-size: 22px; }
.sub { color: #5C6E64; font-size: 11px; margin-bottom: 12px; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #E4EBE6; padding: 8px; text-align: right; font-size: 13px; }
th { background: #7C3AED; color: white; }
.thumb { width: 56px; height: 56px; object-fit: cover; border-radius: 8px; }
.price { color: #7C3AED; font-weight: 800; }
.total-row td { background: #F5F3FF; font-weight: 900; font-size: 15px; color: #7C3AED; }
.footer { margin-top: 18px; font-size: 11px; color: #5C6E64; text-align: center; }
.print\\:hidden { display: none; }
</style></head><body>${node.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const buildShareText = () => {
    const lines = [];
    lines.push("🛒 قائمة شراء — جملتي");
    if (companyName) lines.push(`إلى الشركة: ${companyName}`);
    lines.push(`التاريخ: ${new Date().toLocaleDateString("en-GB")}`);
    lines.push("─".repeat(20));
    items.forEach((it, i) => {
      const cost = it.product._override_cost ?? it.product.cost_price ?? 0;
      lines.push(`${i + 1}. ${itemName(it)}\n   سعر الشراء: ${fmt(cost)} د.ع × ${it.cartons} كرتون = ${fmt(lineTotal(it))} د.ع`);
    });
    lines.push("─".repeat(20));
    lines.push(`إجمالي الكراتين: ${totalCartons}`);
    lines.push(`المجموع الكلي: ${fmt(grandTotal)} د.ع`);
    lines.push("https://jumlaty.online");
    return lines.join("\n");
  };

  const handleWhatsApp = () => {
    if (items.length === 0) { toast.error("اختر مواد أولاً"); return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, "_blank");
  };

  // Push this purchase list to the admin dashboard as a proposal. Admin can
  // then approve (stock + cost_price updates) or reject. Manager does NOT
  // touch the live inventory directly.
  const [exporting, setExporting] = useState(false);
  const handleExportToAdmin = async () => {
    if (items.length === 0) { toast.error("اختر مواد أولاً"); return; }
    if (!supplierId) {
      toast.error("اختر اسم الشركة (المُجهّز) قبل التصدير");
      return;
    }
    if (!await confirmDialog(
      `سيُرسل اقتراح الشراء إلى لوحة الإدارة:\n` +
      `• المُجهّز: ${selectedSupplier?.name || "—"}\n` +
      `• ${items.length} صنف\n` +
      `• ${totalCartons} كرتون\n` +
      `• ${fmt(grandTotal)} د.ع\n\n` +
      `الإدارة ستراجع وتوافق ليُضاف للمخزون ويُسجَّل المبلغ على حساب المُجهّز. هل تريد الإرسال؟`
    )) return;
    setExporting(true);
    try {
      const payload = {
        supplier_id: supplierId,
        items: items.map((it) => ({
          product_id: it.product.id,
          display_name: it.display_name != null ? it.display_name : null,
          cost_price: it.product._override_cost ?? it.product.cost_price ?? 0,
          cartons: Number(it.cartons || 0),
        })),
      };
      await axios.post(`${API}/admin/purchase-proposals`, payload);
      toast.success("تم إرسال الاقتراح إلى لوحة الإدارة ✓");
      // Reset local state so the manager can start a new list
      setPicked({});
      setSupplierId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الإرسال");
    } finally { setExporting(false); }
  };

  if (loading) {
    return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#7C3AED]" /></div>;
  }

  return (
    <div className="space-y-3" data-testid="purchase-list-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#7C3AED] to-[#A855F7] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingCart className="w-5 h-5" />
          <h3 className="font-bold text-lg">قائمة الشراء</h3>
        </div>
        <p className="text-xs opacity-90">
          أنشئ قائمة شراء غير رسمية لإرسالها إلى شركة المُورِّد. تتضمّن سعر الشراء وعدد الكراتين والمجموع الكلّي.
          هذه القائمة <span className="font-bold">لا تؤثّر على المخزون</span>.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <div className="relative">
            <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7C3AED] pointer-events-none" />
            <select
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
              data-testid="purchase-supplier-select"
              className={`w-full pr-9 px-3 py-2 rounded-xl border-2 bg-white text-sm font-bold ${
                supplierId ? "border-[#A855F7] text-[#1E3A5F]" : "border-[#E85D45] text-[#5C6E64]"
              }`}
            >
              <option value="">— اختر اسم الشركة (المُجهّز) —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{Number(s.debt || 0) > 0 ? `  ·  مديونية: ${fmt(s.debt)} د.ع` : ""}
                </option>
              ))}
            </select>
          </div>
          {!supplierId && (
            <p className="text-[10px] text-[#E85D45] mt-1 font-bold">
              ⚠ يجب اختيار شركة من «حسابات المُجهّزين» قبل التصدير. (إن لم تكن مسجّلة، أضِفها من لوحة الإدارة أولاً)
            </p>
          )}
          {selectedSupplier && (
            <p className="text-[10px] text-[#5C6E64] mt-1">
              {selectedSupplier.phone && (<>📞 <span dir="ltr">{selectedSupplier.phone}</span> · </>)}
              المديونية الحالية:
              <span className="font-bold text-[#E85D45] mx-1" dir="ltr">
                {fmt(Math.max(0, Number(selectedSupplier.debt || 0)))} د.ع
              </span>
            </p>
          )}
        </div>
        <label className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2 cursor-pointer self-start">
          <input
            type="checkbox"
            checked={showImages}
            onChange={(e) => setShowImages(e.target.checked)}
            data-testid="purchase-show-images"
            className="w-4 h-4 accent-[#7C3AED]"
          />
          <ImageIcon className="w-4 h-4 text-[#5C6E64]" />
          <span className="text-sm">عرض صور المواد</span>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowPicker(true)} data-testid="purchase-open-picker"
          className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
          <ListChecks className="w-4 h-4" /> اختيار مواد
        </button>
        <button onClick={handlePrint} disabled={items.length === 0} data-testid="purchase-print-btn"
          className="bg-[#1E3A5F] hover:bg-[#152a45] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
          <Printer className="w-4 h-4" /> طباعة / PDF
        </button>
        <button onClick={handleWhatsApp} disabled={items.length === 0} data-testid="purchase-whatsapp-btn"
          className="bg-[#25D366] hover:bg-[#1ebe57] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
          <Share2 className="w-4 h-4" /> مشاركة على واتساب
        </button>
        <button onClick={handleExportToAdmin} disabled={items.length === 0 || exporting || !supplierId}
          data-testid="purchase-export-to-admin-btn"
          title={!supplierId ? "اختر شركة المُجهّز أولاً" : "إرسال إلى لوحة الإدارة لتنزيلها على المخزون بعد الموافقة"}
          className="bg-[#1E3A5F] hover:bg-[#152a45] disabled:bg-[#BAE6FD] disabled:cursor-not-allowed text-white font-bold py-2 px-4 rounded-full flex items-center gap-1">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
          تصدير إلى لوحة الإدارة
        </button>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-2xl p-4 border-2 border-[#E4EBE6]" ref={printRef} data-testid="purchase-preview">
        <h1>🛒 قائمة شراء — جملتي</h1>
        <div className="sub">
          {companyName && (<>إلى الشركة: <b>{companyName}</b> · </>)}
          التاريخ: <span dir="ltr">{new Date().toLocaleDateString("en-GB")}</span>
          {" · "} عدد الأصناف: <span dir="ltr">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <p className="text-center text-[#5C6E64] text-sm py-8">لم يتم اختيار مواد بعد. اضغط "اختيار مواد".</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                {showImages && <th style={{ width: 72 }}>الصورة</th>}
                <th>اسم المادة</th>
                <th style={{ width: 120 }}>سعر الشراء</th>
                <th style={{ width: 120 }} className="print:hidden">سعر A+</th>
                <th style={{ width: 120 }} className="print:hidden">سعر B+</th>
                <th style={{ width: 100 }}>الكراتين</th>
                <th style={{ width: 130 }}>المجموع</th>
                <th style={{ width: 50 }} className="print:hidden">حذف</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const cost = it.product._override_cost ?? it.product.cost_price ?? 0;
                const customName = it.display_name != null;
                return (
                  <tr key={it.product.id} data-testid={`purchase-row-${it.product.id}`}>
                    <td dir="ltr" className="text-center">{i + 1}</td>
                    {showImages && (
                      <td className="text-center">
                        {it.product.image_path ? (
                          <img src={`${API}/files/${it.product.image_path}`} alt="" className="thumb" />
                        ) : <span style={{ color: "#5C6E64" }}>—</span>}
                      </td>
                    )}
                    <td>
                      {/* Editable name — defaults to product name, can be shortened
                          per-row WITHOUT mutating the inventory record. */}
                      <input
                        value={itemName(it)}
                        onChange={(e) => setDisplayName(it.product.id, e.target.value)}
                        data-testid={`purchase-name-${it.product.id}`}
                        placeholder={it.product.name_ar}
                        className="w-full px-2 py-1 rounded border border-[#A855F7] text-sm font-bold print:border-0 print:bg-transparent"
                      />
                      {customName && (
                        <div className="text-[10px] text-[#5C6E64] mt-1 flex items-center gap-1 print:hidden">
                          <span>الأصلي: {it.product.name_ar}</span>
                          <button
                            type="button"
                            onClick={() => setPicked((p) => {
                              const next = { ...p[it.product.id] };
                              delete next.display_name;
                              return { ...p, [it.product.id]: next };
                            })}
                            data-testid={`purchase-name-reset-${it.product.id}`}
                            className="text-[#3B82F6] hover:underline"
                          >
                            ↺ استعادة
                          </button>
                        </div>
                      )}
                      {it.product.weight_per_piece && (
                        <span className="text-[10px] text-[#5C6E64] block mt-0.5 print:inline print:mt-0 print:mr-1">· <span dir="ltr">{it.product.weight_per_piece}</span></span>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        value={cost}
                        onChange={(e) => setCostPrice(it.product.id, e.target.value)}
                        data-testid={`purchase-cost-${it.product.id}`}
                        className="w-full px-2 py-1 rounded border border-[#A855F7] text-sm font-bold text-[#7C3AED] print:border-0 print:bg-transparent"
                        dir="ltr"
                      />
                    </td>
                    {/* 🏷️ A+ price — updates the product's main `price` field on blur */}
                    <td className="print:hidden">
                      <input
                        type="number"
                        value={it.product._pending_price ?? it.product.price ?? 0}
                        onChange={(e) => setPriceAPlus(it.product.id, e.target.value)}
                        onBlur={() => saveTierPrice(it.product, "price")}
                        data-testid={`purchase-price-a-${it.product.id}`}
                        title="سعر البيع لزبائن A+ — يُحفظ تلقائياً عند مغادرة الحقل"
                        className="w-full px-2 py-1 rounded border-2 border-emerald-400 text-sm font-bold text-emerald-700 bg-emerald-50/40"
                        dir="ltr"
                      />
                    </td>
                    {/* 🏷️ B+ price — updates `wholesale_price` on blur */}
                    <td className="print:hidden">
                      <input
                        type="number"
                        value={it.product._pending_wholesale_price ?? it.product.wholesale_price ?? 0}
                        onChange={(e) => setPriceBPlus(it.product.id, e.target.value)}
                        onBlur={() => saveTierPrice(it.product, "wholesale_price")}
                        data-testid={`purchase-price-b-${it.product.id}`}
                        title="سعر البيع لزبائن B+ (جملة) — يُحفظ تلقائياً عند مغادرة الحقل"
                        className="w-full px-2 py-1 rounded border-2 border-orange-400 text-sm font-bold text-orange-700 bg-orange-50/40"
                        dir="ltr"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        value={it.cartons}
                        onChange={(e) => setCartons(it.product.id, e.target.value)}
                        data-testid={`purchase-cartons-${it.product.id}`}
                        className="w-full px-2 py-1 rounded border border-[#1E3A5F] text-sm font-bold text-[#1E3A5F] print:border-0 print:bg-transparent"
                        dir="ltr"
                      />
                    </td>
                    <td className="price" dir="ltr">{fmt(lineTotal(it))} د.ع</td>
                    <td className="print:hidden text-center">
                      <button onClick={() => removeItem(it.product.id)} className="text-[#E85D45] hover:bg-[#E85D45]/10 rounded p-1"><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                );
              })}
              <tr className="total-row">
                <td colSpan={showImages ? 4 : 3} className="text-left">المجموع الكلّي</td>
                <td className="print:hidden"></td>{/* spacer for A+ column (screen only) */}
                <td className="print:hidden"></td>{/* spacer for B+ column (screen only) */}
                <td dir="ltr">{totalCartons} كرتون</td>
                <td dir="ltr">{fmt(grandTotal)} د.ع</td>
                <td className="print:hidden"></td>
              </tr>
            </tbody>
          </table>
        )}
        {items.length > 0 && (
          <div className="footer">قائمة شراء غير رسمية — لا تؤثّر على المخزون · jumlaty.online</div>
        )}
      </div>

      {showPicker && (
        <ProductPickerModal
          products={products}
          picked={picked}
          onClose={() => setShowPicker(false)}
          onDone={(d) => { setPicked(d); setShowPicker(false); toast.success(`تم إرفاق ${Object.keys(d).length} مادة`); }}
        />
      )}
    </div>
  );
};

export default PurchaseListTab;
